import { useState } from 'react';
import { Button, OtpInput } from '@/components/ui';
import { useOtpStore, useOtpSubmitting, useOtpError } from '@/store/otpStore';

const TEST_ID_PREFIX = 'withdraw-verification';
const OTP_LENGTH = 6;

/**
 * Inline OTP verification step of the withdraw flow. Rendered in place of the
 * withdraw form while an OTP challenge is pending (see `WithdrawForm`). Reads
 * the OTP state from `useOtpStore`; submitting calls `submitOtp` to resolve the
 * in-flight interceptor promise. The back affordance lives in `WithdrawForm`
 * (top-left, above the stepper) and calls `cancelOtp`.
 *
 * The global `<OtpModal>` is suppressed by the `useOtpSlot()` registration in
 * `WalletPage`, so this is the only OTP surface while on the withdraw tab.
 */
export function WithdrawVerification() {
  const submitting = useOtpSubmitting();
  const error = useOtpError();
  const submitOtp = useOtpStore((s) => s.submitOtp);

  const [code, setCode] = useState('');

  function handleSubmit(value: string) {
    if (value.length !== OTP_LENGTH || submitting) return;
    submitOtp(value);
  }

  return (
    <div className="flex flex-col items-center gap-[15px] pt-2 text-center" role="group" aria-label="Verification">
      <p className="text-[16px] leading-[22px] text-ink">
        A text message with a verification code has been sent to your phone number. Check your phone &amp; enter your
        verification code.
      </p>

      <div className="flex w-full flex-col items-center gap-[10px]">
        <p className="text-[16.2px] font-light text-ink">Please enter the code here</p>
        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={handleSubmit}
          length={OTP_LENGTH}
          disabled={submitting}
          autoFocus
          className="justify-center gap-[10px]"
          fieldClassName="size-auto h-[52px] w-[44px] rounded-[10px]! border-[1.5px] border-ink-muted bg-surface-soft text-ink focus:border-purple focus:ring-purple/40"
        />
      </div>

      {error === 'invalid' && (
        <p data-testid={`${TEST_ID_PREFIX}-error`} className="text-[12.6px] text-danger-alt" role="alert">
          Invalid code. Please try again.
        </p>
      )}

      {/* TODO: wire "Resend code" once the api exposes a resend endpoint. The
          OTP is sent by the backend as part of the request that raised the
          challenge, and there's no handle to re-trigger it, so the link is
          shown disabled rather than as a dead action. */}
      <p className="flex items-center justify-center gap-2 text-[14.4px] text-ink">
        Did not get a code?
        <span className="font-semibold text-muted underline">Resend code</span>
      </p>

      <Button
        data-testid={`${TEST_ID_PREFIX}-submit-button`}
        size="lg"
        className="w-full"
        onClick={() => handleSubmit(code)}
        disabled={code.length !== OTP_LENGTH || submitting}
        type="button"
      >
        {submitting ? 'Verifying…' : 'Verify'}
      </Button>
    </div>
  );
}
